import * as React from 'react';
import type { VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { CalendlyWidget } from '@/components/ui/calendly-widget';
import { trackWebsiteEvent } from '@/lib/analytics';

/* -------------------------------------------------------------------------- */
/*  Global "Book a demo" dialog with a prerendered Calendly widget.           */
/*                                                                            */
/*  A single, app-wide dialog (mounted once by <BookDemoProvider>) holds one  */
/*  Calendly inline widget that is rendered up-front and kept warm, so opening */
/*  the dialog from any "Book a demo" CTA reuses the loaded calendar. The    */
/*  iframe stays mounted across page navigation and tab switches. The shared */
/*  CalendlyWidget owns the embed URL / theme / height.                      */
/* -------------------------------------------------------------------------- */

const TITLE_ID = 'book-demo-dialog-title';

/* -------------------------------------------------------------------------- */
/*  Provider + context                                                        */
/* -------------------------------------------------------------------------- */

type BookDemoContextValue = { open: (location?: string) => void };

const BookDemoContext = React.createContext<BookDemoContextValue>({
  open: () => {},
});

/** Open the shared "Book a demo" dialog from any trigger. */
export function useBookDemo(): BookDemoContextValue {
  return React.useContext(BookDemoContext);
}

/**
 * Mounts the single, app-wide "Book a demo" dialog and prerenders its Calendly
 * widget. Wrap the app once (see Layout). Children call {@link useBookDemo} to
 * open it.
 */
export function BookDemoProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  // Snapshot the opening context once even when multiple triggers share a dialog.
  const openRef = React.useRef(open);
  openRef.current = open;
  const analyticsContext = React.useRef({
    cta_location: 'content',
    demo_page_path: '',
  });

  const ctx = React.useMemo<BookDemoContextValue>(
    () => ({
      open: (location = 'content') => {
        if (!openRef.current) {
          analyticsContext.current = {
            cta_location: location,
            demo_page_path: window.location.pathname,
          };
          trackWebsiteEvent('demo_opened', analyticsContext.current);
        }
        openRef.current = true;
        setOpen(true);
      },
    }),
    []
  );

  return (
    <BookDemoContext.Provider value={ctx}>
      {children}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        keepMounted
        labelledById={TITLE_ID}
        className="max-w-lg bg-surface"
      >
        {/* Title shrinks + softens (slate-600, medium) once the scheduler is
            ready, so it recedes behind the widget. */}
        <h2
          id={TITLE_ID}
          className={cn(
            'text-center transition-all duration-300',
            ready
              ? 'text-base font-medium text-slate-600'
              : 'text-lg font-semibold text-ink-900'
          )}
        >
          Schedule a demo
        </h2>

        <CalendlyWidget
          active={open}
          analyticsContext={analyticsContext.current}
          onReadyChange={setReady}
          className="mt-4 rounded-3xl border border-hairline"
        />
      </Dialog>
    </BookDemoContext.Provider>
  );
}

/* -------------------------------------------------------------------------- */
/*  Trigger                                                                   */
/* -------------------------------------------------------------------------- */

type BookDemoButtonProps = VariantProps<typeof buttonVariants> &
  Omit<React.ComponentPropsWithoutRef<'button'>, 'className'> & {
    className?: string;
    analyticsLocation?: string;
  };

/**
 * Drop-in replacement for the "Book a demo" CTA `<Button>`: opens the shared,
 * prerendered demo dialog. Forwards Button styling (variant/size/className) and
 * any extra onClick (e.g. to also close the mobile menu).
 */
export function BookDemoButton({
  children = 'Book a demo',
  onClick,
  analyticsLocation = 'content',
  ...props
}: BookDemoButtonProps) {
  const { open } = useBookDemo();
  return (
    <Button
      type="button"
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) open(analyticsLocation);
      }}
      {...props}
    >
      {children}
    </Button>
  );
}
